import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an `<img>` has failed to load, including failures React never hears about.
 *
 * These sections are server-rendered, so an image can finish failing before React attaches its
 * listeners — and `error` does not bubble, so the delegated `onError` never fires. Checking
 * `complete && naturalWidth === 0` on mount catches exactly that case: an image the browser has
 * finished with that has no intrinsic size has failed.
 *
 * Without it a missing logo shows the browser's broken-image icon, which is the one outcome the
 * fallbacks exist to prevent.
 */
export const useImageFallback = (src?: string) => {
  const ref = useRef<HTMLImageElement>(null);
  const [hasFailed, setHasFailed] = useState(false);

  // A tile can be recycled onto a different entry as a list re-renders; without resetting, the
  // previous entry's failure would suppress the new entry's perfectly good logo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs per image, which is the point
  useEffect(() => {
    setHasFailed(false);

    const image = ref.current;
    if (image?.complete && image.naturalWidth === 0) setHasFailed(true);
  }, [src]);

  return { ref, hasFailed, onError: () => setHasFailed(true) };
};
